import { ChangeEvent, useRef, useState } from 'react'
import clsx from 'clsx';
import Plus from 'shared/Icons/Plus';
import ImageCropModal from '../ImageCropModal';

interface CropResolution {
  width: number;
  height: number;
}

function getResolutionFromDimension(dimension?: string): CropResolution | undefined {
  if (!dimension) {
    return undefined
  }
  const numbers = (dimension.match(/\d+/g) || []).map(value => Number(value))
  if (numbers.length < 2) {
    return undefined
  }
  return {
    width: numbers[0],
    height: numbers[1]
  }
}

interface UploadWithPreviewProps {
  placeholder?: string;
  label?: string;
  onFileChange(files: File[]): any;
  accept?: string;
  multiple?: boolean;
  value?: string;
  onRemove?(): void;
  error?: string;
  disabled?: boolean;
  round?: boolean;
  className?: string;
  withoutOverlay?: boolean;
  dimension?: string;
  cropResolution?: CropResolution;
  cropTitle?: string;
  disableAutoCropFromDimension?: boolean;
}

export default function UploadWithPreview({
  placeholder = "Upload",
  label = "Image",
  onFileChange,
  accept = '*/*',
  multiple,
  value,
  onRemove,
  error = '',
  disabled = false,
  round = false,
  withoutOverlay = false,
  className,
  dimension,
  cropResolution,
  cropTitle,
  disableAutoCropFromDimension = false
}: UploadWithPreviewProps) {

  const uploadRef = useRef<HTMLInputElement>(null)
  const [openCropModal, setOpenCropModal] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const selectedResolution = cropResolution || (!disableAutoCropFromDimension ? getResolutionFromDimension(dimension) : undefined)

  function onFileSelection(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    const selectedFile = files[0]
    if (selectedResolution && selectedFile && files.length === 1 && selectedFile.type.startsWith('image/')) {
      setPendingFile(selectedFile)
      setOpenCropModal(true)
      return
    }
    onFileChange(files)
  }

  function handleRemove() {
    if (!disabled && onRemove) {
      onRemove()
    }
  }

  const uploadClass = error ? 'uploader error' : 'uploader'

  return (
    <>
      <div className={clsx("uploader-container", className)}>
        <input
          onChange={onFileSelection}
          onClick={event => (event.target as HTMLInputElement).value = ''}
          type="file"
          style={{ display: 'none' }}
          ref={uploadRef}
          accept={accept}
          multiple={multiple}
        />
        {
          value ?
            <div className={clsx("uploader-preview", round && 'round')}>
              {
                !withoutOverlay &&
                <div className="label-overlay">
                  {label}
                </div>
              }
              <img src={value} alt="Uploader Preview" />
              {
                !disabled &&
                <div className="close-icon" title="Remove" onClick={handleRemove}>
                  <h6>X</h6>
                </div>
              }
            </div>
            :
            <div className={clsx(uploadClass, round && 'round')} onClick={() => uploadRef.current?.click()}>
              <Plus fontSize={32} color='#B7B5C6' />
              <p>
                <div className='mb-1'>{placeholder}</div>
                {dimension && <div>{dimension}</div>}
              </p>
              <p style={{ color: 'red' }} >{error}</p>
            </div>
        }
      </div>
      {
        selectedResolution &&
        <ImageCropModal
          open={openCropModal}
          file={pendingFile}
          resolution={selectedResolution}
          title={cropTitle}
          onClose={() => {
            setPendingFile(null)
            setOpenCropModal(false)
          }}
          onApply={(file) => {
            onFileChange([file])
            setPendingFile(null)
            setOpenCropModal(false)
          }}
        />
      }
    </>
  )
}
